/**
 * SMS Communications - Type Definitions
 * Types specific to SMS communication management
 */

import type { AppSMSModelView } from '@actschurch/ccms-sdk';
import type { AppSMSTemplateModelView } from '@actschurch/ccms-sdk';

// ============================================================================
// SDK Model Interfaces (imported from SDK)
// ============================================================================

export type { AppSMSModelView, AppSMSTemplateModelView };

// ============================================================================
// SMS Queue Item (with required ID for DataTable)
// ============================================================================

export type SmsQueueRow = AppSMSModelView & {
  id: number; // messageID mapped to id for DataTable compatibility
  queuedDateTime?: string | number | null; // normalized from the API's createdDateTime
};

export type SmsTemplateRow = AppSMSTemplateModelView & {
  id: number; // Map template ID to id for DataTable compatibility
};

// ============================================================================
// Component Props Interfaces
// ============================================================================

export interface SmsQueueTabProps {
  queueItems: AppSMSModelView[];
  loading?: boolean;
  onRefresh: () => void;
  onView: (item: AppSMSModelView) => void;
  onEdit: (item: AppSMSModelView) => void;
  onDelete: (id: number) => Promise<void>;
  /** Deletes the whole selection in a single request. */
  onBulkDelete: (ids: number[]) => Promise<unknown>;
  onRetry: (id: number) => Promise<void>;
  onUpdatePriority: (id: number, priority: number) => Promise<void>;
}

export interface SmsTemplatesTabProps {
  templates: AppSMSTemplateModelView[];
  loading?: boolean;
  onRefresh: () => void;
  onView: (template: AppSMSTemplateModelView) => void;
  onEdit: (template: AppSMSTemplateModelView) => void;
  onDelete: (id: number) => Promise<void>;
  onCreate: () => void;
}

export interface SmsTestTabProps {
  templates: AppSMSTemplateModelView[];
  loading?: boolean;
  onSend: (data: SmsTestData) => Promise<void>;
}

export interface SmsQueueModalProps {
  opened: boolean;
  onClose: () => void;
  item: AppSMSModelView | null;
  mode: 'view' | 'edit';
  onSave?: (data: Partial<AppSMSModelView>) => Promise<void>;
  loading?: boolean;
}

export interface SmsTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  template: AppSMSTemplateModelView | null;
  mode: 'view' | 'edit' | 'create';
  onSave?: (data: Partial<AppSMSTemplateModelView>) => Promise<void>;
  loading?: boolean;
}

// ============================================================================
// Form Data Interfaces
// ============================================================================

export interface SmsTestData {
  useTemplate: boolean;
  templateId?: number;
  cellNumber: string;
  message: string;
  priority: number;
  scheduledDateTime?: Date;
  addToQueue: boolean; // true = queue, false = send now
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Case-insensitive pick of the first defined value among candidate keys. */
function pick(obj: Record<string, any>, ...keys: string[]): any {
  for (const k of keys) if (obj[k] != null) return obj[k];
  const lower: Record<string, any> = {};
  for (const k of Object.keys(obj)) lower[k.toLowerCase()] = obj[k];
  for (const k of keys) { const v = lower[k.toLowerCase()]; if (v != null) return v; }
  return undefined;
}

export function mapSmsToRow(sms: AppSMSModelView): SmsQueueRow {
  // The list endpoint returns the backend AppSMSModelView whose field names differ from the
  // UI/SDK model: SMSID→messageID, PhoneNumber→cellNumber, DeliveryStatus→status,
  // SentDate→dateSent, CreatedDateTime→queuedDateTime. Normalize here (case-insensitive,
  // since JSON casing of SMSID is policy-dependent) so the row id (delete/edit/retry),
  // Recipient, Status, Queued columns, member-lookup, and search all read the right values.
  const raw = sms as Record<string, any>;
  const id = pick(raw, 'messageID', 'smsid', 'id');
  return {
    ...sms,
    id,
    messageID: id,
    cellNumber: pick(raw, 'cellNumber', 'phoneNumber', 'toNumber') ?? null,
    status: pick(raw, 'status', 'deliveryStatus') ?? null,
    dateSent: pick(raw, 'dateSent', 'sentDate') ?? null,
    queuedDateTime: pick(raw, 'queuedDateTime', 'createdDateTime') ?? null,
  } as SmsQueueRow;
}

export function mapTemplateToRow(template: AppSMSTemplateModelView): SmsTemplateRow {
  return {
    ...template,
    id: template.templateID,
  };
}

// ============================================================================
// SMS-Specific Helpers
// ============================================================================

export function validatePhoneNumber(phone: string): boolean {
  // International format: +[country code][number]
  // Example: +27821234567
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  return phoneRegex.test(phone);
}

export function formatPhoneNumber(phone: string): string {
  if (!phone) return '';
  // Basic formatting for display
  if (phone.startsWith('+')) {
    const country = phone.slice(0, 3);
    const rest = phone.slice(3);
    return `${country} ${rest.slice(0, 3)} ${rest.slice(3)}`;
  }
  return phone;
}

/**
 * Decode an SMS message body for display. SMS bodies are stored inconsistently:
 * template-derived messages are base64-encoded, while compose/campaign messages are
 * stored as plaintext. Blindly calling atob() on plaintext throws InvalidCharacterError
 * and crashes the component, so detect base64 and fall back to the raw string otherwise.
 */
export function decodeSmsMessage(raw?: string | null): string {
  const s = raw || '';
  if (!s) return '';
  const t = s.trim();
  // base64 never contains whitespace, uses the base64 charset, and is a multiple of 4.
  if (/\s/.test(t) || t.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(t)) return s;
  try {
    const bytes = Uint8Array.from(atob(t), (c) => c.charCodeAt(0));
    // fatal:true rejects bytes that aren't valid UTF-8 — i.e. plaintext that merely
    // looked like base64 (e.g. "Test") — so those fall through to the raw string.
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return s;
  }
}

export function getCharacterCount(message: string): { count: number; segments: number } {
  const count = message.length;
  // SMS segments: 160 chars for single, 153 chars per segment for multi
  const segments = count <= 160 ? 1 : Math.ceil(count / 153);
  return { count, segments };
}
